import { IOrderItem } from "interfaces";
import { Link } from "react-router-dom";
import Button from "components/button";
import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import { getOrderStatusLabel, getPaymentMethodLabel, getTicketPlatformLabel, isHaveActionPermission } from "utills/helpers";
import { TooltipParagraph } from "components/tooltipParagraph";
import moment from "moment";
import { Tag } from "components/tag";

interface OrderTableProps {
  data?: IOrderItem[];
  pageSize: number;
  currentPage: number;
}

export const OrderTable = ({
  data = [],
  pageSize = 10,
  currentPage = 1,
}: OrderTableProps) => {
  return (
    <div className="flow-root">
      <div className="relative overflow-x-auto min-w-full">
        <table className="table-auto w-max min-w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
          <thead>
            <tr>
              <th scope="col" className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8">
                No
              </th>
              <th scope="col" className="th">
                Order code
              </th>
              <th scope="col" className="th">
                Group name/Buyer email & phone
              </th>
              <th scope="col" className="th">
                Ticket name
              </th>
              <th scope="col" className="th">
                Event name
              </th>
              <th scope="col" className="th">
                Status
              </th>
              <th scope="col" className="th">
                VAT
              </th>
              <th scope="col" className="th">
                Payment Method
              </th>
              <th scope="col" className="th">
                Coupon code
              </th>
              <th scope="col" className="th">
                Created at
              </th>
              <th scope="col" className="th">
                Payment at
              </th>
              <th scope="col" className="th">
                E-ticket usage date
              </th>
              <th scope="col" className="th">
                Ticket platform
              </th>
              <th scope="col" className="th w-20">
                <span className="sr-only">Action</span>
                {/* <p className="">Action</p> */}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((item: IOrderItem, index: number) => (
              <tr key={index}>
                <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                  {(currentPage - 1) * pageSize + 1 + index}
                </td>
                <td className="td">
                  <Link to={`/orders/${item.code}`} className="w-fit font-medium text-brand-500 hover:underline">
                    {item.code}
                  </Link>
                </td>
                <td className="td max-w-60 overflow-x-auto" style={{wordWrap: "break-word"}}>
                  {item.group_name ? <p className="font-semibold">{item.group_name}</p> : <></>}
                  <p>{item.buyer_email}</p>
                  <p>{item.buyer_phone}</p>
                </td>
                <td className="td max-w-60 overflow-x-auto">
                  <TooltipParagraph 
                    text={item.item_description_en}
                    className="whitespace-pre-wrap max-w-[280px]"
                  />
                </td>
                <td className="td max-w-60 overflow-x-auto">
                  <TooltipParagraph 
                    text={item.event_name_en}
                    className="whitespace-pre-wrap max-w-[280px]"
                  />
                </td>
                <td className="td">
                  <Tag text={getOrderStatusLabel(item.order_status)} type={item.order_status}  />
                </td>
                <td className="td w-20">
                  {item.is_vat ? "Yes" : "No"}
                </td>
                <td className="td">
                  {getPaymentMethodLabel(item.payment_method)}
                </td>
                <td className="td" style={{wordBreak: "break-word"}}>
                  {item.coupon_codes.join(",")}
                </td>
                <td className="td w-32">
                  {item.created_at ? moment(item.created_at).format("DD/MM/YYYY HH:mm:ss") : ""}
                </td>
                <td className="td w-32">
                  {item.purchased_at ? moment(item.purchased_at).format("DD/MM/YYYY HH:mm:ss") : ""}
                </td>
                <td className="td">
                  {item.ticket_use_for_date ? moment(item.ticket_use_for_date).format("DD/MM/YYYY") : ""}
                  {item.showtime ? ` - ${item.showtime}` : ""}
                </td>
                <td className="td">
                  {getTicketPlatformLabel(item.ticket_platform)}
                </td>
                <td className="td gap-x-2 pl-4 pr-4 sm:pr-6 lg:pr-8">
                  <div className="flex items-center gap-x-2">
                    <Link to={`/orders/${item.code}`} className="w-fit">
                      <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                        <EyeIcon className="w-4 h-4 text-black" />
                      </Button>
                    </Link>
                    {isHaveActionPermission("", "orders", "edit") ? (
                      <Link
                        to={`/orders/${item.code}/edit`}
                        className="w-fit"
                      >
                        <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                          <PencilIcon className="w-4 h-4 text-black" />
                        </Button>
                      </Link>
                    ) : <></>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
